import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { User, Shield, Send, RefreshCw, CheckCircle2, AlertCircle, Info, Zap, Globe, Bell, Activity } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const AVAILABLE_INTERVALS = [
  '1m', '3m', '5m', '15m', '30m', '1h', '4h', '1d'
];

const AVAILABLE_STRATEGIES = [
  // ── 묶음전략 전용 ──
  { id: 'strider_dca', name: 'Strider DCA (BTC 급락 분할매수)', stars: 3, group: 'multi' },

  // ── Multi-Symbol Scanner (기본) ──
  { id: 'tsp_stoch_realtime', name: 'TSP Stochastic Realtime', stars: 3, group: 'multi' },
  { id: 'stochastic_ema', name: 'Stochastic + EMA 10', stars: 3, group: 'multi' },
  { id: 'perfect_alignment_pullback', name: 'Perfect Alignment Pullback', stars: 3, group: 'multi' },
  { id: 'macd_ema', name: 'MACD + EMA 20', stars: 3, group: 'multi' },
  { id: 'kewma', name: 'KEWMA', stars: 3, group: 'multi' },
  { id: 'auto3_signal', name: 'Auto3 Signal', stars: 3, group: 'multi' },
  { id: 'auto7_signal', name: 'Auto7 Signal', stars: 3, group: 'multi' },
  { id: 'ema_momentum_score', name: 'EMA Momentum Score', stars: 3, group: 'multi' },
  { id: 'tsp_hybrid_dca', name: 'TSP Hybrid DCA', stars: 3, group: 'multi' },

  { id: 'zerolag_macd', name: 'ZeroLag MACD', stars: 2, group: 'multi' },
  { id: 'hull_macd', name: 'Hull MACD', stars: 2, group: 'multi' },
  { id: 'channel_trend', name: 'Channel Trend', stars: 2, group: 'multi' },
  { id: 'channel_trend2', name: 'Channel Trend 2', stars: 2, group: 'multi' },
  { id: 'hybrid_pullback', name: 'Hybrid Pullback', stars: 2, group: 'multi' },
  { id: 'cen_ichi_kewma', name: 'CenIchi Kewma', stars: 2, group: 'multi' },
  { id: 'motion_mfi', name: 'Motion MFI', stars: 2, group: 'multi' },

  { id: 'super_hybrid', name: 'Super Hybrid', stars: 1, group: 'multi' },
  { id: 'auto_signal', name: 'Auto Signal', stars: 1, group: 'multi' },
  { id: 'auto4_signal', name: 'Auto4 Signal', stars: 1, group: 'multi' },
  { id: 'v_inversion', name: 'V-Inversion', stars: 1, group: 'multi' },

  // ── Surge Scanner (기타 알트 전용) ──
  { id: 'surge', name: 'Surge (기타 알트 숏, RSI 되돌림)', stars: 2, group: 'alt' },
  { id: 'score_based_signal', name: 'Score-Based (기타 알트, 멀티시그널)', stars: 2, group: 'alt' },
];

interface ProfilePanelProps {
  user: {
    name?: string;
    email: string;
    receive_auto_signals?: boolean;
    auto_signal_filters?: {
      symbols?: string[];
      intervals?: string[];
      strategies?: string[];
      max_active_bots?: number;
      entry_pct?: number;
      entry_type?: string;
      entry_amount?: number;
      allocated_balance?: number;
      leverage?: number;
      trading_mode?: string;
      tp_pct?: number;
      sl_pct?: number;
      symbol_configs?: Record<string, { leverage?: number; tp_pct?: number; sl_pct?: number }>;
    };
    profile: {
      binance_api_key?: string;
      binance_api_secret?: string;
      binance_testnet_api_key?: string;
      binance_testnet_api_secret?: string;
      telegram_bot_token?: string;
      telegram_chat_id?: string;
      okx_api_key?: string;
      okx_api_secret?: string;
      okx_passphrase?: string;
      okx_demo_api_key?: string;
      okx_demo_api_secret?: string;
      okx_demo_passphrase?: string;
      bybit_api_key?: string;
      bybit_api_secret?: string;
      bybit_testnet_api_key?: string;
      bybit_testnet_api_secret?: string;
      bitget_api_key?: string;
      bitget_api_secret?: string;
      bitget_passphrase?: string;
      bitget_testnet_api_key?: string;
      bitget_testnet_api_secret?: string;
      bitget_testnet_passphrase?: string;
    };
  };
  activeBotsCount: number;
  disable_all_testnet?: boolean;
  enabled_exchanges?: Record<string, boolean>;
  maxBots?: number;
  autoScannerEnabled?: boolean;
  autoScannerMaxActiveSessions?: string | number;
  autoScannerExchange?: string;
  autoScannerTargetSymbols?: string[];
}

export const ProfilePanel: React.FC<ProfilePanelProps> = ({ user, activeBotsCount, disable_all_testnet, enabled_exchanges, maxBots, autoScannerEnabled, autoScannerMaxActiveSessions, autoScannerExchange, autoScannerTargetSymbols = [] }) => {
  const [selectedStars, setSelectedStars] = useState<number | 'all'>('all');
  const [formData, setFormData] = useState({
    receive_auto_signals: user.receive_auto_signals ?? false,
    auto_signal_filters: {
      symbols: user.auto_signal_filters?.symbols ?? [],
      intervals: user.auto_signal_filters?.intervals ?? [],
      strategies: user.auto_signal_filters?.strategies ?? [],
      max_active_bots: user.auto_signal_filters?.max_active_bots,
      entry_pct: user.auto_signal_filters?.entry_pct,
      entry_type: user.auto_signal_filters?.entry_type ?? 'fixed',
      entry_amount: user.auto_signal_filters?.entry_amount,
      allocated_balance: user.auto_signal_filters?.allocated_balance,
      leverage: user.auto_signal_filters?.leverage,
      trading_mode: user.auto_signal_filters?.trading_mode ?? 'paper',
      tp_pct: user.auto_signal_filters?.tp_pct,
      sl_pct: user.auto_signal_filters?.sl_pct,
      symbol_configs: user.auto_signal_filters?.symbol_configs ?? {}
    },
    binance_api_key: user.profile.binance_api_key || '',
    binance_api_secret: user.profile.binance_api_secret || '',
    binance_testnet_api_key: user.profile.binance_testnet_api_key || '',
    binance_testnet_api_secret: user.profile.binance_testnet_api_secret || '',
    telegram_bot_token: user.profile.telegram_bot_token || '',
    telegram_chat_id: user.profile.telegram_chat_id || '',
    okx_api_key: user.profile.okx_api_key || '',
    okx_api_secret: user.profile.okx_api_secret || '',
    okx_passphrase: user.profile.okx_passphrase || '',
    okx_demo_api_key: user.profile.okx_demo_api_key || '',
    okx_demo_api_secret: user.profile.okx_demo_api_secret || '',
    okx_demo_passphrase: user.profile.okx_demo_passphrase || '',
    bybit_api_key: user.profile.bybit_api_key || '',
    bybit_api_secret: user.profile.bybit_api_secret || '',
    bybit_testnet_api_key: user.profile.bybit_testnet_api_key || '',
    bybit_testnet_api_secret: user.profile.bybit_testnet_api_secret || '',
    bitget_api_key: user.profile.bitget_api_key || '',
    bitget_api_secret: user.profile.bitget_api_secret || '',
    bitget_passphrase: user.profile.bitget_passphrase || '',
    bitget_testnet_api_key: user.profile.bitget_testnet_api_key || '',
    bitget_testnet_api_secret: user.profile.bitget_testnet_api_secret || '',
    bitget_testnet_passphrase: user.profile.bitget_testnet_passphrase || ''
  });

  const [symbolsInput, setSymbolsInput] = useState(
    (user.auto_signal_filters?.symbols || []).join(', ')
  );

  const handleFilterNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const num = value === '' ? undefined : parseFloat(value);
    setFormData(prev => ({
      ...prev,
      auto_signal_filters: {
        ...prev.auto_signal_filters,
        [name]: num
      }
    }));
  };

  const handleSymbolsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSymbolsInput(val);
    const parsed = val.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
    setFormData(prev => ({
      ...prev,
      auto_signal_filters: {
        ...prev.auto_signal_filters,
        symbols: parsed
      }
    }));
  };

  const handleSymbolToggle = (symbol: string) => {
    const current = formData.auto_signal_filters.symbols || [];
    const next = current.includes(symbol)
      ? current.filter(s => s !== symbol)
      : [...current, symbol];
    setFormData(prev => ({
      ...prev,
      auto_signal_filters: {
        ...prev.auto_signal_filters,
        symbols: next
      }
    }));
    setSymbolsInput(next.join(', '));
  };

  const handleSelectAllSymbols = () => {
    setFormData(prev => ({
      ...prev,
      auto_signal_filters: {
        ...prev.auto_signal_filters,
        symbols: [...autoScannerTargetSymbols]
      }
    }));
    setSymbolsInput(autoScannerTargetSymbols.join(', '));
  };

  const handleClearAllSymbols = () => {
    setFormData(prev => ({
      ...prev,
      auto_signal_filters: {
        ...prev.auto_signal_filters,
        symbols: []
      }
    }));
    setSymbolsInput('');
  };

  const handleIntervalToggle = (interval: string) => {
    const current = formData.auto_signal_filters.intervals || [];
    const next = current.includes(interval)
      ? current.filter(i => i !== interval)
      : [...current, interval];
    setFormData(prev => ({
      ...prev,
      auto_signal_filters: {
        ...prev.auto_signal_filters,
        intervals: next
      }
    }));
  };

  const handleStrategyToggle = (strategyId: string) => {
    const current = formData.auto_signal_filters.strategies || [];
    const next = current.includes(strategyId)
      ? current.filter(s => s !== strategyId)
      : [...current, strategyId];
    setFormData(prev => ({
      ...prev,
      auto_signal_filters: {
        ...prev.auto_signal_filters,
        strategies: next
      }
    }));
  };

  const handleMaxActiveBotsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const num = val === '' ? undefined : parseInt(val, 10);
    setFormData(prev => ({
      ...prev,
      auto_signal_filters: {
        ...prev.auto_signal_filters,
        max_active_bots: num
      }
    }));
  };

  const [accountData, setAccountData] = useState({
    name: user.name || '',
    email: user.email,
    current_password: '',
    password: '',
    password_confirmation: ''
  });

  const [loading, setLoading] = useState(false);
  const [accLoading, setAccLoading] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [showRestartHint, setShowRestartHint] = useState(false);
  const [showLeverageModal, setShowLeverageModal] = useState(false);
  const [testingStatus, setTestingStatus] = useState<Record<string, { loading: boolean; message: string; success: boolean | null }>>({});

  const testConnection = async (exchangeKey: string) => {
    const isTestnet = exchangeKey.endsWith('_testnet') || exchangeKey.endsWith('_demo');
    const exchange = exchangeKey.replace('_testnet', '').replace('_demo', '');

    let api_key = '';
    let api_secret = '';
    let passphrase = '';

    if (exchangeKey === 'binance') {
      api_key = formData.binance_api_key;
      api_secret = formData.binance_api_secret;
    } else if (exchangeKey === 'binance_testnet') {
      api_key = formData.binance_testnet_api_key;
      api_secret = formData.binance_testnet_api_secret;
    } else if (exchangeKey === 'okx') {
      api_key = formData.okx_api_key;
      api_secret = formData.okx_api_secret;
      passphrase = formData.okx_passphrase;
    } else if (exchangeKey === 'okx_demo') {
      api_key = formData.okx_demo_api_key;
      api_secret = formData.okx_demo_api_secret;
      passphrase = formData.okx_demo_passphrase;
    } else if (exchangeKey === 'bybit') {
      api_key = formData.bybit_api_key;
      api_secret = formData.bybit_api_secret;
    } else if (exchangeKey === 'bybit_testnet') {
      api_key = formData.bybit_testnet_api_key;
      api_secret = formData.bybit_testnet_api_secret;
    } else if (exchangeKey === 'bitget') {
      api_key = formData.bitget_api_key;
      api_secret = formData.bitget_api_secret;
      passphrase = formData.bitget_passphrase;
    } else if (exchangeKey === 'bitget_testnet') {
      api_key = formData.bitget_testnet_api_key;
      api_secret = formData.bitget_testnet_api_secret;
      passphrase = formData.bitget_testnet_passphrase;
    }

    setTestingStatus(prev => ({
      ...prev,
      [exchangeKey]: { loading: true, message: '연결 확인 중...', success: null }
    }));

    try {
      const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;
      const res = await fetch('/dca/check_api_connection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || ''
        },
        body: JSON.stringify({
          exchange,
          api_key,
          api_secret,
          passphrase,
          is_testnet: isTestnet
        })
      });

      const data = await res.json();
      setTestingStatus(prev => ({
        ...prev,
        [exchangeKey]: { loading: false, message: data.message, success: data.ok }
      }));
    } catch (err: any) {
      setTestingStatus(prev => ({
        ...prev,
        [exchangeKey]: { loading: false, message: `요청 실패: ${err.message}`, success: false }
      }));
    }
  };

  const renderTestConnectionButton = (exchangeKey: string) => {
    const status = testingStatus[exchangeKey] || { loading: false, message: '', success: null };
    return (
      <div className="pt-2 border-t border-border/30 mt-3 flex flex-col gap-2">
        <div className="flex items-center justify-between gap-4">
          <Button
            type="button"
            size="sm"
            onClick={() => testConnection(exchangeKey)}
            disabled={status.loading}
            className="h-8 font-black text-[10px] bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-lg px-3 flex-shrink-0"
          >
            {status.loading ? (
              <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1.5" />
            ) : (
              <Activity className="w-3.5 h-3.5 mr-1.5 text-primary" />
            )}
            연결 확인
          </Button>
          {status.message && (
            <span className={`text-[10px] font-bold text-right leading-tight ${status.success ? 'text-emerald-500' : 'text-destructive'}`}>
              {status.message}
            </span>
          )}
        </div>
      </div>
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAccChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAccountData({ ...accountData, [e.target.name]: e.target.value });
  };

  const handleSave = () => {
    if (autoScannerMaxActiveSessions) {
      const globalMax = parseInt(String(autoScannerMaxActiveSessions), 10);
      const userMax = formData.auto_signal_filters.max_active_bots;
      if (globalMax > 0 && userMax && userMax > globalMax) {
        alert(`동시 가동 시그널 봇 제한은 글로벌 제한(${globalMax}개)을 초과할 수 없습니다.`);
        return;
      }
    }

    setLoading(true);
    router.post('/dca/update_profile', { profile: formData }, {
      onSuccess: () => {
        setLoading(false);
        if (activeBotsCount > 0) {
          setShowRestartHint(true);
        }
      },
      onError: () => setLoading(false)
    });
  };

  const handleSaveAccount = () => {
    if (accountData.password && accountData.password !== accountData.password_confirmation) {
      alert('새 비밀번호와 확인 비밀번호가 일치하지 않습니다.');
      return;
    }
    if (accountData.password && !accountData.current_password) {
      alert('비밀번호를 변경하려면 현재 비밀번호를 입력해야 합니다.');
      return;
    }

    setAccLoading(true);
    router.post('/dca/update_account', accountData, {
      onSuccess: () => {
        setAccLoading(false);
        setAccountData({ ...accountData, current_password: '', password: '', password_confirmation: '' });
      },
      onError: () => setAccLoading(false)
    });
  };

  const handleRestartBots = async () => {
    if (!confirm(`${activeBotsCount}개의 실행 중인 봇을 재가동하시겠습니까?`)) return;
    setRestarting(true);
    try {
      const res = await fetch('/dca/restart_active_bots', { method: 'POST', headers: { 'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content } });
      const result = await res.json();
      if (result.success) {
        alert(result.message);
        setShowRestartHint(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setRestarting(false);
    }
  };

  return (
    <div className="space-y-6 pb-20 max-w-2xl mx-auto">
      <div className="flex flex-col gap-1 px-1">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-black text-foreground flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            내 계정 설정
          </h2>
          {maxBots !== undefined && (
            <Badge variant="secondary" className="px-3 py-1 bg-primary/10 text-primary border-none font-bold text-[10px]">
              최대 봇 개수 제한: {maxBots}개
            </Badge>
          )}
        </div>
        <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
          {user.email} 계정의 거래소 및 알림 연동 정보를 관리합니다.
        </p>
      </div>

      {showRestartHint && (
        <Alert className="bg-primary/5 border-primary/20 animate-in fade-in slide-in-from-top-4 duration-300">
          <RefreshCw className="w-4 h-4 text-primary" />
          <AlertTitle className="text-primary font-black text-xs">봇 재시작 필요</AlertTitle>
          <AlertDescription className="text-[11px] font-medium text-muted-foreground mt-1 flex flex-col gap-3">
            API 설정이 변경되었습니다. 현재 실행 중인 {activeBotsCount}개의 봇에 변경 사항을 적용하려면 재시작이 필요합니다.
            <Button
              size="sm"
              onClick={handleRestartBots}
              disabled={restarting}
              className="w-full bg-primary text-white font-black h-9 shadow-lg shadow-primary/20"
            >
              {restarting ? <RefreshCw className="w-3 h-3 animate-spin mr-2" /> : <Zap className="w-3 h-3 mr-2" />}
              실행 중인 봇 모두 재시작
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="exchanges" className="w-full">
        <TabsList className="grid w-full grid-cols-4 bg-muted/50 p-1 rounded-2xl h-12 mb-6">
          <TabsTrigger value="exchanges" className="rounded-xl font-black text-xs data-[state=active]:bg-card data-[state=active]:shadow-sm transition-all">
            <Globe className="w-4 h-4 mr-2" />
            Exchanges
          </TabsTrigger>
          <TabsTrigger value="signals" className="rounded-xl font-black text-xs data-[state=active]:bg-card data-[state=active]:shadow-sm transition-all">
            <Zap className="w-4 h-4 mr-2" />
            Signal
          </TabsTrigger>
          <TabsTrigger value="notifications" className="rounded-xl font-black text-xs data-[state=active]:bg-card data-[state=active]:shadow-sm transition-all">
            <Bell className="w-4 h-4 mr-2" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="account" className="rounded-xl font-black text-xs data-[state=active]:bg-card data-[state=active]:shadow-sm transition-all">
            <Shield className="w-4 h-4 mr-2" />
            Account
          </TabsTrigger>
        </TabsList>

        <TabsContent value="exchanges" className="space-y-6 mt-0">
          {/* Binance Live Keys */}
          {enabled_exchanges?.binance !== false && (
            <Card className="border-border/50 shadow-sm overflow-hidden">
              <CardHeader className="bg-muted/30 border-b border-border/50 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-warning" />
                    <CardTitle className="text-xs font-black uppercase tracking-widest">Binance Futures (Live)</CardTitle>
                  </div>
                  <a
                    href="https://m.blog.naver.com/sari_song94/222581454625"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-2.5 py-1 text-[10px] font-black text-amber-600 dark:text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 rounded-lg transition-all border border-amber-500/20 flex items-center gap-1"
                  >
                    💡 API 발급 가이드
                  </a>
                </div>
                <CardDescription className="text-[10px] font-medium mt-1.5">실전 매매용 바이낸스 선물 API 키 정보를 입력하세요.</CardDescription>
              </CardHeader>
              <CardContent className="p-5 space-y-4">
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">API Key</Label>
                  <Input
                    name="binance_api_key"
                    value={formData.binance_api_key}
                    onChange={handleChange}
                    placeholder="바이낸스 API 키"
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">API Secret</Label>
                  <Input
                    name="binance_api_secret"
                    type="password"
                    value={formData.binance_api_secret}
                    onChange={handleChange}
                    placeholder={user.profile.binance_api_secret ? '********' : '바이낸스 API 시크릿'}
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                {renderTestConnectionButton('binance')}
              </CardContent>
            </Card>
          )}

          {/* Binance Testnet Keys */}
          {enabled_exchanges?.binance !== false && !disable_all_testnet && (
            <Card className="border-border/50 shadow-sm overflow-hidden">
              <CardHeader className="bg-muted/30 border-b border-border/50 py-4">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="h-4 px-1 text-[8px] font-black uppercase tracking-tight bg-amber-500/5 text-amber-600 border-amber-500/20">Testnet</Badge>
                  <CardTitle className="text-xs font-black uppercase tracking-widest">Binance Futures (Testnet)</CardTitle>
                </div>
                <CardDescription className="text-[10px] font-medium">테스트넷 모의 매매용 바이낸스 API 키 정보를 입력하세요.</CardDescription>
              </CardHeader>
              <CardContent className="p-5 space-y-4">
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">Testnet API Key</Label>
                  <Input
                    name="binance_testnet_api_key"
                    value={formData.binance_testnet_api_key}
                    onChange={handleChange}
                    placeholder="바이낸스 테스트넷 API 키"
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">Testnet API Secret</Label>
                  <Input
                    name="binance_testnet_api_secret"
                    type="password"
                    value={formData.binance_testnet_api_secret}
                    onChange={handleChange}
                    placeholder={user.profile.binance_testnet_api_secret ? '********' : '바이낸스 테스트넷 API 시크릿'}
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                {renderTestConnectionButton('binance_testnet')}
              </CardContent>
            </Card>
          )}

          {/* OKX Live Keys */}
          {enabled_exchanges?.okx === true && (
            <Card className="border-border/50 shadow-sm overflow-hidden">
              <CardHeader className="bg-muted/30 border-b border-border/50 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary" />
                    <CardTitle className="text-xs font-black uppercase tracking-widest">OKX (Live)</CardTitle>
                  </div>
                  <a
                    href="https://coinbotx.kr/blog/insights/okx-api-%ED%82%A4-%EB%B0%9C%EA%B8%89-%EB%B0%A9%EB%B2%95-%EC%99%84%EB%B2%BD-%EA%B0%80%EC%9D%B4%EB%93%9C-%EB%8D%B0%EB%AA%A8%EB%B6%80%ED%84%B0-%EC%8B%A4%EA%B1%B0%EB%9E%98%EA%B9%8C%EC%A7%80-202/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-2.5 py-1 text-[10px] font-black text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-all border border-primary/20 flex items-center gap-1"
                  >
                    💡 API 발급 가이드
                  </a>
                </div>
                <CardDescription className="text-[10px] font-medium mt-1.5">실전 매매용 OKX API 키 및 비밀번호를 입력하세요.</CardDescription>
              </CardHeader>
              <CardContent className="p-5 space-y-4">
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">API Key</Label>
                  <Input
                    name="okx_api_key"
                    value={formData.okx_api_key}
                    onChange={handleChange}
                    placeholder="OKX API 키"
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">API Secret</Label>
                  <Input
                    name="okx_api_secret"
                    type="password"
                    value={formData.okx_api_secret}
                    onChange={handleChange}
                    placeholder={user.profile.okx_api_secret ? '********' : 'OKX API 시크릿'}
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">Passphrase</Label>
                  <Input
                    name="okx_passphrase"
                    type="password"
                    value={formData.okx_passphrase}
                    onChange={handleChange}
                    placeholder={user.profile.okx_passphrase ? '********' : 'OKX API 패스프레이즈'}
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                {renderTestConnectionButton('okx')}
              </CardContent>
            </Card>
          )}

          {/* OKX Demo Keys */}
          {enabled_exchanges?.okx === true && !disable_all_testnet && (
            <Card className="border-border/50 shadow-sm overflow-hidden">
              <CardHeader className="bg-muted/30 border-b border-border/50 py-4">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="h-4 px-1 text-[8px] font-black uppercase tracking-tight bg-primary/5 text-primary border-primary/20">Demo</Badge>
                  <CardTitle className="text-xs font-black uppercase tracking-widest">OKX Demo Trading</CardTitle>
                </div>
                <CardDescription className="text-[10px] font-medium">OKX 데모 트레이딩용 API 정보입니다.</CardDescription>
              </CardHeader>
              <CardContent className="p-5 space-y-4">
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">Demo API Key</Label>
                  <Input
                    name="okx_demo_api_key"
                    value={formData.okx_demo_api_key}
                    onChange={handleChange}
                    placeholder="데모 API 키"
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">Demo API Secret</Label>
                  <Input
                    name="okx_demo_api_secret"
                    type="password"
                    value={formData.okx_demo_api_secret}
                    onChange={handleChange}
                    placeholder={user.profile.okx_demo_api_secret ? '********' : '데모 API 시크릿'}
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">Demo Passphrase</Label>
                  <Input
                    name="okx_demo_passphrase"
                    type="password"
                    value={formData.okx_demo_passphrase}
                    onChange={handleChange}
                    placeholder={user.profile.okx_demo_passphrase ? '********' : '데모 API 패스프레이즈'}
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                {renderTestConnectionButton('okx_demo')}
              </CardContent>
            </Card>
          )}

          {/* Bybit Live Keys */}
          {enabled_exchanges?.bybit === true && (
            <Card className="border-border/50 shadow-sm overflow-hidden">
              <CardHeader className="bg-muted/30 border-b border-border/50 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary" />
                    <CardTitle className="text-xs font-black uppercase tracking-widest">Bybit (Live)</CardTitle>
                  </div>
                  <a
                    href="https://rok0188.tistory.com/entry/Bybit-API-%EB%B0%9C%EA%B8%89-%EB%B0%A9%EA%B8%B0"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-2.5 py-1 text-[10px] font-black text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-all border border-primary/20 flex items-center gap-1"
                  >
                    💡 API 발급 가이드
                  </a>
                </div>
                <CardDescription className="text-[10px] font-medium mt-1.5">실전 매매용 Bybit API 키 정보를 입력하세요.</CardDescription>
              </CardHeader>
              <CardContent className="p-5 space-y-4">
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">API Key</Label>
                  <Input
                    name="bybit_api_key"
                    value={formData.bybit_api_key}
                    onChange={handleChange}
                    placeholder="바이비트 API 키"
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">API Secret</Label>
                  <Input
                    name="bybit_api_secret"
                    type="password"
                    value={formData.bybit_api_secret}
                    onChange={handleChange}
                    placeholder={user.profile.bybit_api_secret ? '********' : '바이비트 API 시크릿'}
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                {renderTestConnectionButton('bybit')}
              </CardContent>
            </Card>
          )}

          {/* Bybit Testnet Keys */}
          {enabled_exchanges?.bybit === true && !disable_all_testnet && (
            <Card className="border-border/50 shadow-sm overflow-hidden">
              <CardHeader className="bg-muted/30 border-b border-border/50 py-4">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="h-4 px-1 text-[8px] font-black uppercase tracking-tight bg-amber-500/5 text-amber-600 border-amber-500/20">Testnet</Badge>
                  <CardTitle className="text-xs font-black uppercase tracking-widest">Bybit (Testnet)</CardTitle>
                </div>
                <CardDescription className="text-[10px] font-medium">테스트넷 모의 매매용 Bybit API 키 정보를 입력하세요.</CardDescription>
              </CardHeader>
              <CardContent className="p-5 space-y-4">
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">Testnet API Key</Label>
                  <Input
                    name="bybit_testnet_api_key"
                    value={formData.bybit_testnet_api_key}
                    onChange={handleChange}
                    placeholder="바이비트 테스트넷 API 키"
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">Testnet API Secret</Label>
                  <Input
                    name="bybit_testnet_api_secret"
                    type="password"
                    value={formData.bybit_testnet_api_secret}
                    onChange={handleChange}
                    placeholder={user.profile.bybit_testnet_api_secret ? '********' : '바이비트 테스트넷 API 시크릿'}
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                {renderTestConnectionButton('bybit_testnet')}
              </CardContent>
            </Card>
          )}
          {/* Bitget Live Keys */}
          {enabled_exchanges?.bitget === true && (
            <Card className="border-border/50 shadow-sm overflow-hidden">
              <CardHeader className="bg-muted/30 border-b border-border/50 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary" />
                    <CardTitle className="text-xs font-black uppercase tracking-widest">Bitget (Live)</CardTitle>
                  </div>
                  <a
                    href="https://blog.naver.com/memememo_official/223571822646"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-2.5 py-1 text-[10px] font-black text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-all border border-primary/20 flex items-center gap-1"
                  >
                    💡 API 발급 가이드
                  </a>
                </div>
                <CardDescription className="text-[10px] font-medium mt-1.5">실전 매매용 Bitget API 키 정보를 입력하세요.</CardDescription>
              </CardHeader>
              <CardContent className="p-5 space-y-4">
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">API Key</Label>
                  <Input
                    name="bitget_api_key"
                    value={formData.bitget_api_key}
                    onChange={handleChange}
                    placeholder="비트겟 API 키"
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">API Secret</Label>
                  <Input
                    name="bitget_api_secret"
                    type="password"
                    value={formData.bitget_api_secret}
                    onChange={handleChange}
                    placeholder={user.profile.bitget_api_secret ? '********' : '비트겟 API 시크릿'}
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">Passphrase</Label>
                  <Input
                    name="bitget_passphrase"
                    type="password"
                    value={formData.bitget_passphrase}
                    onChange={handleChange}
                    placeholder={user.profile.bitget_passphrase ? '********' : '비트겟 API 패스프레이즈'}
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                {renderTestConnectionButton('bitget')}
              </CardContent>
            </Card>
          )}

          {/* Bitget Testnet Keys */}
          {enabled_exchanges?.bitget === true && !disable_all_testnet && (
            <Card className="border-border/50 shadow-sm overflow-hidden">
              <CardHeader className="bg-muted/30 border-b border-border/50 py-4">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="h-4 px-1 text-[8px] font-black uppercase tracking-tight bg-amber-500/5 text-amber-600 border-amber-500/20">Testnet</Badge>
                  <CardTitle className="text-xs font-black uppercase tracking-widest">Bitget (Testnet)</CardTitle>
                </div>
                <CardDescription className="text-[10px] font-medium">테스트넷 모의 매매용 Bitget API 키 정보를 입력하세요.</CardDescription>
              </CardHeader>
              <CardContent className="p-5 space-y-4">
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">Testnet API Key</Label>
                  <Input
                    name="bitget_testnet_api_key"
                    value={formData.bitget_testnet_api_key}
                    onChange={handleChange}
                    placeholder="비트겟 테스트넷 API 키"
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">Testnet API Secret</Label>
                  <Input
                    name="bitget_testnet_api_secret"
                    type="password"
                    value={formData.bitget_testnet_api_secret}
                    onChange={handleChange}
                    placeholder={user.profile.bitget_testnet_api_secret ? '********' : '비트겟 테스트넷 API 시크릿'}
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">Testnet Passphrase</Label>
                  <Input
                    name="bitget_testnet_passphrase"
                    type="password"
                    value={formData.bitget_testnet_passphrase}
                    onChange={handleChange}
                    placeholder={user.profile.bitget_testnet_passphrase ? '********' : '비트겟 테스트넷 API 패스프레이즈'}
                    className="text-xs font-mono bg-muted/20"
                  />
                </div>
                {renderTestConnectionButton('bitget_testnet')}
              </CardContent>
            </Card>
          )}


          {/* Explanation for Paper Trading vs Testnet */}
          {!disable_all_testnet && (
            <div className="bg-amber-500/5 border border-amber-500/10 p-4 rounded-2xl space-y-2">
              <div className="flex items-center gap-2 text-amber-600">
                <Info className="w-4 h-4" />
                <h4 className="text-[11px] font-black uppercase tracking-tight">시뮬레이션(Paper Trading) 및 테스트넷 안내</h4>
              </div>
              <p className="text-[10px] font-medium text-muted-foreground leading-relaxed">
                * 봇 설정의 **시뮬레이션(Paper Trading)**은 실제 거래소의 Testnet/Demo 환경에 접속하는 것이 아니며, 서비스 엔진 내에서 가상으로 매매를 시뮬레이션합니다.<br />
                * 입력하신 **Testnet/Demo API 키**는 서버가 별도의 테스트 모드로 구동될 때 실제 거래소 테스트 환경 연동을 위해 사용됩니다. 일반적인 시뮬레이션 시에는 사용되지 않으므로 참고 부탁드립니다.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <Button
              disabled={loading}
              onClick={handleSave}
              className="w-full bg-foreground text-background font-black h-12 shadow-xl hover:opacity-90 active:scale-95 transition-all rounded-2xl"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              거래소 및 알림 설정 저장
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="signals" className="space-y-6 mt-0">
          <Card className="border-border/50 shadow-sm overflow-hidden">
            <CardHeader className="bg-muted/30 border-b border-border/50 py-4">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" />
                <CardTitle className="text-xs font-black uppercase tracking-widest">Auto Signal 받기</CardTitle>
              </div>
              <CardDescription className="text-[10px] font-medium">
                관리자가 발송하는 자동 스캐너 시그널 봇을 자동으로 생성/수신합니다.
                {autoScannerExchange && (
                  <span className="block mt-1 font-bold text-primary">
                    📡 신호 분석 거래소 (출처): <span className="uppercase underline font-black">{autoScannerExchange} 메인넷</span>
                  </span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-5 space-y-6">
              {autoScannerEnabled === false && (
                <Alert className="bg-amber-500/5 border-amber-500/20 rounded-xl mb-4 p-3 flex gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div className="space-y-0.5">
                    <p className="text-[11px] font-black text-amber-600">Auto Signal 비활성화됨 (관리자)</p>
                    <p className="text-[10px] text-muted-foreground font-medium">현재 관리자에 의해 전역 자동 스캐너 신호 발송이 비활성화(OFF) 상태입니다. 설정을 활성화하더라도 신호가 수신되지 않습니다.</p>
                  </div>
                </Alert>
              )}

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="text-sm font-bold">Auto Signal 수신</Label>
                  <p className="text-[10px] text-muted-foreground">켜면 새 시그널이 감지될 때 자동으로 봇이 생성됩니다.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="receive_auto_signals"
                    className="sr-only peer"
                    checked={formData.receive_auto_signals ?? false}
                    onChange={(e) => setFormData({ ...formData, receive_auto_signals: e.target.checked })}
                  />
                  <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:bg-primary peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                </label>
              </div>

              {formData.receive_auto_signals && (
                <div className="pt-4 border-t border-border/50 space-y-5 animate-in fade-in slide-in-from-top-2 duration-200">
                  {/* 최대 활성 봇 개수 */}
                  <div className="grid gap-2">
                    <Label className="text-[11px] font-bold text-foreground">
                      최대 동시 가동 Auto Signal 봇 개수
                    </Label>
                    <Input
                      type="number"
                      name="max_active_bots"
                      value={formData.auto_signal_filters.max_active_bots ?? ''}
                      onChange={handleMaxActiveBotsChange}
                      placeholder="입력 시 해당 개수로 제한 (비워두면 무제한)"
                      className="text-[10px] bg-muted/20 font-bold"
                    />
                    <p className="text-[10px] text-amber-600 dark:text-amber-400 font-bold mt-0.5">
                      ⚠️ 현재 서버의 글로벌 시그널 봇 최대 가동 제한: <span className="underline font-black">{autoScannerMaxActiveSessions || '제한 없음'}</span>개
                      {autoScannerExchange && (
                        <> | 대상 거래소: <span className="underline font-black uppercase">{autoScannerExchange}</span></>
                      )}
                    </p>
                  </div>

                  {/* 매매 모드, 진입비중, 사용시드, 레버리지 설정 */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="grid gap-1.5">
                      <Label className="text-[11px] font-bold text-foreground">매매 모드 (Trading Mode)</Label>
                      <select
                        name="trading_mode"
                        value={formData.auto_signal_filters.trading_mode ?? 'paper'}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            auto_signal_filters: {
                              ...prev.auto_signal_filters,
                              trading_mode: val
                            }
                          }));
                        }}
                        className="flex h-9 w-full rounded-md border border-input bg-muted/20 px-3 py-1 text-[10px] font-bold shadow-xs transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <option value="paper">시뮬레이션 (Paper)</option>
                        <option value="testnet">테스트넷 (Testnet)</option>
                        <option value="live">실매매 (Live)</option>
                      </select>
                    </div>
                    <div className="grid gap-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-[11px] font-bold text-foreground">진입 설정</Label>
                        <div className="flex bg-muted rounded-md p-0.5 text-[9px] font-bold">
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                auto_signal_filters: {
                                  ...prev.auto_signal_filters,
                                  entry_type: 'percent'
                                }
                              }));
                            }}
                            className={`px-1.5 py-0.5 rounded-sm transition-all ${(formData.auto_signal_filters.entry_type || 'percent') === 'percent' ? 'bg-background shadow-xs text-foreground font-black' : 'text-muted-foreground'}`}
                          >
                            %
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                auto_signal_filters: {
                                  ...prev.auto_signal_filters,
                                  entry_type: 'fixed'
                                }
                              }));
                            }}
                            className={`px-1.5 py-0.5 rounded-sm transition-all ${formData.auto_signal_filters.entry_type === 'fixed' ? 'bg-background shadow-xs text-foreground font-black' : 'text-muted-foreground'}`}
                          >
                            $
                          </button>
                        </div>
                      </div>
                      <Input
                        type="number"
                        name="entry_amount"
                        value={formData.auto_signal_filters.entry_amount ?? formData.auto_signal_filters.entry_pct ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          const num = val === '' ? undefined : parseFloat(val);
                          setFormData(prev => ({
                            ...prev,
                            auto_signal_filters: {
                              ...prev.auto_signal_filters,
                              entry_amount: num,
                              entry_pct: (prev.auto_signal_filters.entry_type || 'percent') === 'percent' ? num : undefined
                            }
                          }));
                        }}
                        placeholder={(formData.auto_signal_filters.entry_type || 'percent') === 'percent' ? '기본값 (10%)' : '고정 진입 ($)'}
                        className="text-[10px] bg-muted/20 font-bold"
                      />
                    </div>
                    <div className="grid gap-1.5">
                      <Label className="text-[11px] font-bold text-foreground">사용할 시드 (USDT)</Label>
                      <Input
                        type="number"
                        name="allocated_balance"
                        value={formData.auto_signal_filters.allocated_balance ?? ''}
                        onChange={handleFilterNumericChange}
                        placeholder="자동 할당"
                        className="text-[10px] bg-muted/20 font-bold"
                      />
                    </div>
                    <div className="grid gap-1.5">
                      <Label className="text-[11px] font-bold text-foreground">레버리지 (배)</Label>
                      <Input
                        type="number"
                        name="leverage"
                        value={formData.auto_signal_filters.leverage ?? ''}
                        onChange={handleFilterNumericChange}
                        placeholder="자동 설정"
                        className="text-[10px] bg-muted/20 font-bold"
                      />
                      <p
                        onClick={() => setShowLeverageModal(true)}
                        className="text-[8px] text-muted-foreground hover:text-primary transition-colors cursor-pointer font-bold leading-tight mt-0.5 underline decoration-dotted"
                      >
                        (코인별 기본 레버리지 기준)
                      </p>
                    </div>
                  </div>

                  {/* 기본 익절, 기본 손절 설정 */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-1.5">
                      <Label className="text-[11px] font-bold text-foreground">기본 익절 폭 (Target TP %)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="전략 기본값 사용"
                        value={formData.auto_signal_filters.tp_pct ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            auto_signal_filters: {
                              ...prev.auto_signal_filters,
                              tp_pct: val === '' ? undefined : parseFloat(val)
                            }
                          }));
                        }}
                        className="text-[10px] bg-muted/20 font-bold"
                      />
                    </div>
                    <div className="grid gap-1.5">
                      <Label className="text-[11px] font-bold text-foreground">기본 손절 폭 (Stop Loss %)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="전략 기본값 사용"
                        value={formData.auto_signal_filters.sl_pct ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            auto_signal_filters: {
                              ...prev.auto_signal_filters,
                              sl_pct: val === '' ? undefined : parseFloat(val)
                            }
                          }));
                        }}
                        className="text-[10px] bg-muted/20 font-bold"
                      />
                    </div>
                  </div>

                  {/* 코인별 상세 설정 (레버리지, 익절/손절) */}
                  <div className="border-t border-border/50 pt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-[11px] font-bold text-foreground flex items-center gap-1.5">
                        <Globe className="w-3.5 h-3.5 text-primary" />
                        코인별 상세 설정 (개별 레버리지 / 익절 / 손절)
                      </Label>
                      <button
                        type="button"
                        onClick={() => setShowLeverageModal(true)}
                        className="text-[9px] text-primary hover:underline font-bold"
                      >
                        기본 설정 가이드
                      </button>
                    </div>
                    
                    {/* 추가 Form */}
                    <div className="grid grid-cols-4 gap-2 items-end bg-muted/10 p-3 rounded-xl border border-border/50">
                      <div className="grid gap-1">
                        <span className="text-[9px] font-bold text-muted-foreground uppercase">코인</span>
                        <select
                          id="new_config_symbol"
                          className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-0.5 text-[10px] font-bold"
                        >
                          <option value="">선택</option>
                          {autoScannerTargetSymbols.map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div className="grid gap-1">
                        <span className="text-[9px] font-bold text-muted-foreground uppercase">레버리지</span>
                        <Input
                          id="new_config_lev"
                          type="number"
                          placeholder="배"
                          className="h-8 text-[10px] font-bold bg-background"
                        />
                      </div>
                      <div className="grid gap-1">
                        <span className="text-[9px] font-bold text-muted-foreground uppercase">익절 (%)</span>
                        <Input
                          id="new_config_tp"
                          type="number"
                          step="0.1"
                          placeholder="%"
                          className="h-8 text-[10px] font-bold bg-background"
                        />
                      </div>
                      <div className="grid gap-1">
                        <span className="text-[9px] font-bold text-muted-foreground uppercase">손절 (%)</span>
                        <div className="flex gap-1.5">
                          <Input
                            id="new_config_sl"
                            type="number"
                            step="0.1"
                            placeholder="%"
                            className="h-8 text-[10px] font-bold bg-background"
                          />
                          <Button
                            type="button"
                            size="sm"
                            className="h-8 px-2 bg-primary text-white text-[9px] font-black"
                            onClick={() => {
                              const selectEl = document.getElementById('new_config_symbol') as HTMLSelectElement;
                              const levEl = document.getElementById('new_config_lev') as HTMLInputElement;
                              const tpEl = document.getElementById('new_config_tp') as HTMLInputElement;
                              const slEl = document.getElementById('new_config_sl') as HTMLInputElement;
                              
                              const sym = selectEl?.value;
                              if (!sym) return alert('코인을 선택해주세요.');
                              
                              const lev = levEl?.value ? parseInt(levEl.value) : undefined;
                              const tp = tpEl?.value ? parseFloat(tpEl.value) : undefined;
                              const sl = slEl?.value ? parseFloat(slEl.value) : undefined;
                              
                              if (!lev && !tp && !sl) return alert('설정값을 최소 하나 이상 입력해주세요.');
                              
                              setFormData(prev => {
                                const configs = { ...(prev.auto_signal_filters.symbol_configs || {}) };
                                configs[sym] = { leverage: lev, tp_pct: tp, sl_pct: sl };
                                return {
                                  ...prev,
                                  auto_signal_filters: {
                                    ...prev.auto_signal_filters,
                                    symbol_configs: configs
                                  }
                                };
                              });
                              
                              selectEl.value = '';
                              levEl.value = '';
                              tpEl.value = '';
                              slEl.value = '';
                            }}
                          >
                            추가
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* 목록 표시 */}
                    {Object.keys(formData.auto_signal_filters.symbol_configs || {}).length > 0 && (
                      <div className="border border-border/50 rounded-xl overflow-hidden text-[10px] font-bold bg-card">
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr className="bg-muted/30 border-b border-border/50 text-[9px] text-muted-foreground uppercase">
                              <th className="p-2">코인</th>
                              <th className="p-2">레버리지</th>
                              <th className="p-2">익절</th>
                              <th className="p-2">손절</th>
                              <th className="p-2 text-right">삭제</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(formData.auto_signal_filters.symbol_configs || {}).map(([sym, cfg]: [string, any]) => (
                              <tr key={sym} className="border-b border-border/40 hover:bg-muted/5">
                                <td className="p-2 text-foreground font-black">{sym}</td>
                                <td className="p-2">{cfg.leverage ? `${cfg.leverage}배` : '-'}</td>
                                <td className="p-2">{cfg.tp_pct ? `${cfg.tp_pct}%` : '-'}</td>
                                <td className="p-2">{cfg.sl_pct ? `${cfg.sl_pct}%` : '-'}</td>
                                <td className="p-2 text-right">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-1.5 text-danger hover:bg-danger/5 text-[9px] font-black"
                                    onClick={() => {
                                      setFormData(prev => {
                                        const configs = { ...(prev.auto_signal_filters.symbol_configs || {}) };
                                        delete configs[sym];
                                        return {
                                          ...prev,
                                          auto_signal_filters: {
                                            ...prev.auto_signal_filters,
                                            symbol_configs: configs
                                          }
                                        };
                                      });
                                    }}
                                  >
                                    삭제
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* 코인별 필터 */}
                  <div className="grid gap-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[11px] font-bold text-foreground">수신할 코인 심볼</Label>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          type="button"
                          variant="ghost"
                          onClick={handleSelectAllSymbols}
                          className="h-5 px-1.5 text-[9px] font-black text-primary hover:bg-primary/5"
                        >
                          전체 선택
                        </Button>
                        <Button
                          size="sm"
                          type="button"
                          variant="ghost"
                          onClick={handleClearAllSymbols}
                          className="h-5 px-1.5 text-[9px] font-black text-muted-foreground hover:bg-muted"
                        >
                          전체 해제
                        </Button>
                      </div>
                    </div>

                    {/* 관리자가 제공하는 심볼 칩 */}
                    <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-2 border border-border/50 rounded-xl bg-muted/10">
                      {autoScannerTargetSymbols.map((sym) => {
                        const active = formData.auto_signal_filters.symbols?.includes(sym);
                        return (
                          <Button
                            key={sym}
                            size="sm"
                            type="button"
                            variant={active ? 'default' : 'outline'}
                            onClick={() => handleSymbolToggle(sym)}
                            className="h-7 px-2 text-[10px] font-bold rounded-lg transition-all"
                          >
                            {sym}
                          </Button>
                        );
                      })}
                    </div>

                    {/* 관리자가 제공하지 않지만 설정되어 있는 심볼 검사 */}
                    {(() => {
                      const userSymbols = formData.auto_signal_filters.symbols || [];
                      const invalid = userSymbols.filter(s => !autoScannerTargetSymbols.includes(s));
                      if (invalid.length === 0) return null;
                      return (
                        <div className="mt-1 space-y-1.5">
                          <p className="text-[9px] font-black text-amber-600 flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                            관리자 미제공 (비활성) 심볼 감지됨:
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {invalid.map((sym) => (
                              <Button
                                key={sym}
                                size="sm"
                                type="button"
                                variant="destructive"
                                onClick={() => handleSymbolToggle(sym)}
                                className="h-6 px-2 text-[9px] font-bold rounded-lg bg-amber-500 hover:bg-amber-600 border-none text-white transition-all flex items-center gap-1"
                              >
                                {sym} ✕
                              </Button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* 직접 텍스트 입력 창 (양방향 연동됨) */}
                    <div className="mt-1">
                      <Input
                        name="symbols_input"
                        value={symbolsInput}
                        onChange={handleSymbolsChange}
                        placeholder="쉼표로 구분하여 직접 입력도 가능 (예: BTC, ETH)"
                        className="text-[10px] font-mono bg-muted/20 font-bold h-9"
                      />
                    </div>
                    <p className="text-[9px] text-muted-foreground font-medium">아무것도 선택하지 않으면 모든 코인을 수신합니다.</p>
                  </div>

                  {/* 시간대별 필터 */}
                  <div className="grid gap-2">
                    <Label className="text-[11px] font-bold text-foreground">수신할 시간대 (봉 간격)</Label>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {AVAILABLE_INTERVALS.map((interval) => {
                        const active = formData.auto_signal_filters.intervals?.includes(interval);
                        return (
                          <Button
                            key={interval}
                            size="sm"
                            type="button"
                            variant={active ? 'default' : 'outline'}
                            onClick={() => handleIntervalToggle(interval)}
                            className="h-7 px-2.5 text-[10px] font-bold rounded-lg transition-all"
                          >
                            {interval}
                          </Button>
                        );
                      })}
                    </div>
                    <p className="text-[9px] text-muted-foreground font-medium">아무것도 선택하지 않으면 모든 시간대를 수신합니다.</p>
                  </div>

                  {/* 전략별 필터 */}
                  <div className="grid gap-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-[11px] font-bold text-foreground">수신할 전략</Label>
                      <div className="flex items-center gap-1">
                        <span className="text-[9px] text-muted-foreground mr-1">등급 필터:</span>
                        <Button
                          size="sm"
                          type="button"
                          variant={selectedStars === 'all' ? 'default' : 'outline'}
                          onClick={() => setSelectedStars('all')}
                          className="h-5 px-1.5 text-[9px] font-bold rounded"
                        >
                          전체
                        </Button>
                        <Button
                          size="sm"
                          type="button"
                          variant={selectedStars === 3 ? 'default' : 'outline'}
                          onClick={() => setSelectedStars(3)}
                          className="h-5 px-1.5 text-[9px] font-bold rounded text-amber-500"
                        >
                          ⭐⭐⭐
                        </Button>
                        <Button
                          size="sm"
                          type="button"
                          variant={selectedStars === 2 ? 'default' : 'outline'}
                          onClick={() => setSelectedStars(2)}
                          className="h-5 px-1.5 text-[9px] font-bold rounded text-amber-500"
                        >
                          ⭐⭐
                        </Button>
                        <Button
                          size="sm"
                          type="button"
                          variant={selectedStars === 1 ? 'default' : 'outline'}
                          onClick={() => setSelectedStars(1)}
                          className="h-5 px-1.5 text-[9px] font-bold rounded text-amber-500"
                        >
                          ⭐
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-3 pt-1">
                      {/* Multi-Symbol Scanner 전략 */}
                      <div>
                        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5">📡 Multi-Symbol Scanner (기본)</p>
                        <div className="flex flex-wrap gap-1.5">
                          {AVAILABLE_STRATEGIES.filter(s => s.group === 'multi' && (selectedStars === 'all' || s.stars === selectedStars)).map((strat) => {
                            const active = formData.auto_signal_filters.strategies?.includes(strat.id);
                            return (
                              <Button key={strat.id} size="sm" type="button"
                                variant={active ? 'default' : 'outline'}
                                onClick={() => handleStrategyToggle(strat.id)}
                                className="h-7 px-2.5 text-[10px] font-bold rounded-lg transition-all flex items-center gap-1">
                                <span>{strat.name}</span>
                                <span className="text-amber-500 font-mono text-[8px]">{'⭐'.repeat(strat.stars)}</span>
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                      {/* Surge Scanner 전략 (기타 알트 전용) */}
                      <div>
                        <p className="text-[10px] font-bold text-primary uppercase tracking-wider mb-1.5">🔍 Surge Scanner (기타 알트 21~100위 전용)</p>
                        <div className="flex flex-wrap gap-1.5">
                          {AVAILABLE_STRATEGIES.filter(s => s.group === 'alt' && (selectedStars === 'all' || s.stars === selectedStars)).map((strat) => {
                            const active = formData.auto_signal_filters.strategies?.includes(strat.id);
                            return (
                              <Button key={strat.id} size="sm" type="button"
                                variant={active ? 'default' : 'outline'}
                                onClick={() => handleStrategyToggle(strat.id)}
                                className="h-7 px-2.5 text-[10px] font-bold rounded-lg transition-all flex items-center gap-1">
                                <span>{strat.name}</span>
                                <span className="text-amber-500 font-mono text-[8px]">{'⭐'.repeat(strat.stars)}</span>
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <p className="text-[10px] text-muted-foreground font-medium">아무것도 선택하지 않으면 모든 전략을 수신합니다.</p>
                    <p className="text-[9px] text-primary font-bold mt-1 leading-tight">
                      💡 Surge Scanner 전략은 글로벌 신호 봇 제한(기본 {autoScannerMaxActiveSessions || '[제한 없음]'}개)과 별도로 Bitget 21~100위 코인(기타 알트)을 독립 트랙에서 스캔합니다.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex flex-col gap-3">
            <Button
              disabled={loading}
              onClick={handleSave}
              className="w-full bg-foreground text-background font-black h-12 shadow-xl hover:opacity-90 active:scale-95 transition-all rounded-2xl"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              거래소 및 알림 설정 저장
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6 mt-0">
          {/* Telegram Settings */}
          <Card className="border-border/50 shadow-sm overflow-hidden">
            <CardHeader className="bg-muted/30 border-b border-border/50 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Send className="w-4 h-4 text-sky-500" />
                  <CardTitle className="text-xs font-black uppercase tracking-widest">Telegram Notification</CardTitle>
                </div>
                <a
                  href="https://gabrielkim.tistory.com/entry/Telegram-Bot-Token-%EB%B0%8F-Chat-Id-%EC%96%BB%EA%B8%B0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-2.5 py-1 text-[10px] font-black text-sky-600 dark:text-sky-400 bg-sky-500/10 hover:bg-sky-500/20 rounded-lg transition-all border border-sky-500/20 flex items-center gap-1"
                >
                  💡 토큰/챗ID 발급 가이드
                </a>
              </div>
              <CardDescription className="text-[10px] font-medium mt-1.5">
                봇 진행 상태 및 매매 알림을 받을 텔레그램 정보입니다.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-5 space-y-4">
              <div className="grid gap-2">
                <Label className="text-[11px] font-bold">Bot Token</Label>
                <Input
                  name="telegram_bot_token"
                  type="password"
                  value={formData.telegram_bot_token}
                  onChange={handleChange}
                  placeholder={user.profile.telegram_bot_token ? '********' : '123456:ABC-DEF...'}
                  className="text-xs font-mono bg-muted/20"
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-[11px] font-bold">Chat ID</Label>
                <Input
                  name="telegram_chat_id"
                  value={formData.telegram_chat_id}
                  onChange={handleChange}
                  placeholder="알림을 받을 채팅 ID"
                  className="text-xs font-mono bg-muted/20"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col gap-3">
            <Button
              disabled={loading}
              onClick={handleSave}
              className="w-full bg-foreground text-background font-black h-12 shadow-xl hover:opacity-90 active:scale-95 transition-all rounded-2xl"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              거래소 및 알림 설정 저장
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="account" className="space-y-6 mt-0">
          <Card className="border-border/50 shadow-sm overflow-hidden">
            <CardHeader className="bg-muted/30 border-b border-border/50 py-4">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-primary" />
                <CardTitle className="text-xs font-black uppercase tracking-widest">Account Information</CardTitle>
              </div>
              <CardDescription className="text-[10px] font-medium">로그인 이메일 및 비밀번호를 변경할 수 있습니다.</CardDescription>
            </CardHeader>
            <CardContent className="p-5 space-y-4">
              <div className="grid gap-2">
                <Label className="text-[11px] font-bold">User Name</Label>
                <Input
                  name="name"
                  value={accountData.name}
                  onChange={handleAccChange}
                  placeholder="실명 혹은 닉네임"
                  className="text-xs bg-muted/20 font-bold"
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-[11px] font-bold">Email Address</Label>
                <Input
                  name="email"
                  value={accountData.email}
                  onChange={handleAccChange}
                  placeholder="이메일 주소"
                  className="text-xs bg-muted/20 font-bold"
                />
              </div>

              <div className="pt-4 border-t border-border/50 space-y-4">
                <div className="flex items-center gap-2">
                  <Shield className="w-3 h-3 text-muted-foreground" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Change Password</span>
                </div>

                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">Current Password</Label>
                  <Input
                    name="current_password"
                    type="password"
                    value={accountData.current_password}
                    onChange={handleAccChange}
                    placeholder="현재 비밀번호 (비밀번호 변경 시 필수)"
                    className="text-xs bg-muted/20 font-mono"
                  />
                </div>

                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">New Password</Label>
                  <Input
                    name="password"
                    type="password"
                    value={accountData.password}
                    onChange={handleAccChange}
                    placeholder="새 비밀번호 (최소 6자)"
                    className="text-xs bg-muted/20 font-mono"
                  />
                </div>

                <div className="grid gap-2">
                  <Label className="text-[11px] font-bold">Confirm New Password</Label>
                  <Input
                    name="password_confirmation"
                    type="password"
                    value={accountData.password_confirmation}
                    onChange={handleAccChange}
                    placeholder="새 비밀번호 확인"
                    className="text-xs bg-muted/20 font-mono"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col gap-3">
            <Button
              disabled={accLoading}
              onClick={handleSaveAccount}
              className="w-full bg-primary text-white font-black h-12 shadow-xl hover:opacity-90 active:scale-95 transition-all rounded-2xl"
            >
              {accLoading ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              계정 정보 변경 적용
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      <p className="text-[10px] text-center text-muted-foreground font-medium px-4">
        <Info className="w-3 h-3 inline mr-1 opacity-50" />
        입력하신 정보는 데이터베이스에 암호화되어 안전하게 보관됩니다. 시크릿 값은 한 번 저장되면 화면에 다시 표시되지 않으며, 수정 시에만 입력하시면 됩니다.
      </p>

      {showLeverageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-card border border-border rounded-2xl max-w-md w-full mx-4 overflow-hidden shadow-2xl p-6 space-y-4 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-border/50 pb-3">
              <h3 className="text-sm font-black flex items-center gap-2 text-foreground">
                <Info className="w-4 h-4 text-primary" />
                기본 설정 가이드 (레버리지 & 익절/손절)
              </h3>
              <button
                onClick={() => setShowLeverageModal(false)}
                className="text-muted-foreground hover:text-foreground text-xs font-bold"
              >
                닫기
              </button>
            </div>
            
            <div className="text-[11px] text-muted-foreground leading-relaxed space-y-4 font-medium overflow-y-auto max-h-[350px] pr-1">
              {/* 레버리지 규칙 */}
              <div className="space-y-1.5">
                <h4 className="font-black text-foreground uppercase tracking-wider text-[10px]">1. 코인별 기본 레버리지</h4>
                <p className="text-[10px] text-muted-foreground">유저 설정에 [자동 설정]으로 둘 경우 적용되는 기본값입니다.</p>
                <div className="p-3 bg-muted/40 rounded-xl space-y-1.5 font-bold text-foreground text-[10px]">
                  <div className="flex justify-between">
                    <span>메이저 코인 (BTC, ETH, SOL, TRX, XRP)</span>
                    <span className="text-primary font-black">20배 (20x)</span>
                  </div>
                  <div className="flex justify-between">
                    <span>준메이저 코인 (ADA, DOGE, BNB, LTC, LINK, NEAR)</span>
                    <span className="text-primary font-black">15배 (15x)</span>
                  </div>
                  <div className="flex justify-between">
                    <span>지정 알트코인 (ENA, AAVE, HBAR, ONDO, XLM, TAO, DOT)</span>
                    <span className="text-primary font-black">10배 (10x)</span>
                  </div>
                  <div className="flex justify-between">
                    <span>기타 알트코인 (TURBO, WLD 등 그 외 알트)</span>
                    <span className="text-muted-foreground">5배 (5x)</span>
                  </div>
                </div>
              </div>

              {/* 전략별 기본 TP/SL */}
              <div className="space-y-1.5">
                <h4 className="font-black text-foreground uppercase tracking-wider text-[10px]">2. 전략별 기본 익절/손절 비율</h4>
                <p className="text-[10px] text-muted-foreground">유저 설정에 [기본값 사용]으로 둘 경우 전략 내부적으로 작동하는 설정값입니다.</p>
                <div className="p-3 bg-muted/40 rounded-xl space-y-2 text-[10px] font-bold text-foreground">
                  <div className="flex justify-between border-b border-border/30 pb-1 text-[9px] text-muted-foreground uppercase">
                    <span>전략 알고리즘</span>
                    <div className="flex gap-4">
                      <span>익절(TP)</span>
                      <span>손절(SL)</span>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span>CenIchi 4Ema Pullback (Auto1)</span>
                    <span className="text-foreground">2.0% / 1.0%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Hybrid Pullback</span>
                    <span className="text-foreground">2.0% / 1.0%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>ZeroLag MACD</span>
                    <span className="text-foreground">2.0% / 1.5%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>KEWMA (15분봉)</span>
                    <span className="text-foreground">1.5% / 1.5%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Super Hybrid</span>
                    <span className="text-foreground">2.0% / 1.5%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Channel Trend (V1 / V2)</span>
                    <span className="text-foreground">2.0% / 1.0%</span>
                  </div>
                </div>
              </div>

              <p className="text-[9px] text-amber-500 font-bold leading-relaxed">
                ※ 프로필에 특정 레버리지 혹은 기본 익절/손절 값을 입력하시거나, "코인별 상세 설정"을 통해 특정 코인에 개별적으로 지정하시면 해당 사용자 정의 설정이 글로벌 설정보다 우선적으로 자동 적용됩니다.
              </p>
            </div>
            <div className="pt-2 border-t border-border/50">
              <Button
                onClick={() => setShowLeverageModal(false)}
                className="w-full bg-primary text-primary-foreground font-black text-xs h-10 rounded-xl"
              >
                확인했습니다
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
